
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useSitemapFetch } from './useSitemapFetch';
import { useSitemapComparison } from './useSitemapComparison';

export const useSitemapVerification = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState<{
    lastVerified: string | null;
    status: 'idle' | 'success' | 'warning' | 'error';
    message: string | null;
  }>({
    lastVerified: null,
    status: 'idle',
    message: null
  });
  
  const { fetchDomainSitemap, fetchStorageSitemap, cleanXmlContent, saveSitemapToPublic } = useSitemapFetch();
  const { compareSitemapContents, validateXmlStructure } = useSitemapComparison();
  const { toast } = useToast();

  /**
   * Verify sitemap access and validate content
   * If sitemapData is provided, it will save the files to the domain root
   */
  const verifySitemapAccess = async (sitemapData?: any) => {
    try {
      setIsVerifying(true);
      setVerificationDetails(prev => ({
        ...prev,
        status: 'idle',
        message: 'Verification in progress...'
      }));
      
      // Add a cache-busting parameter for all requests
      const cacheBust = `?t=${Date.now()}-${Math.random()}`;
      let domainSitemapContent = '';
      let storageSitemapContent = '';
      
      // If we have sitemap data from a recent generation, save the files to domain root
      if (sitemapData?.sitemapUrl && sitemapData?.robotsUrl) {
        const success = await saveSitemapToPublic(sitemapData);
        
        if (success) {
          toast({
            title: "Sitemap Files Saved",
            description: "Sitemap and robots.txt have been copied to domain root",
            variant: "default",
          });
        } else {
          toast({
            title: "Warning",
            description: "Sitemap files were generated but could not be copied to domain root",
            variant: "default", // Changed from 'warning' to 'default' to fix the TypeScript error
          });
        }
      }
      
      // Allow a moment for files to be processed
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 1. Check domain access
      domainSitemapContent = await fetchDomainSitemap(cacheBust);
      
      // 2. Check the generated sitemap URL accessibility
      if (sitemapData?.sitemapUrl) {
        storageSitemapContent = await fetchStorageSitemap(sitemapData.sitemapUrl, cacheBust);
      }
      
      // 3. Ensure both XML documents are properly cleaned
      domainSitemapContent = cleanXmlContent(domainSitemapContent);
      storageSitemapContent = cleanXmlContent(storageSitemapContent);
      
      // 4. Perform initial validation
      if (domainSitemapContent) {
        const domainValidation = validateXmlStructure(domainSitemapContent);
        if (!domainValidation.isValid) {
          console.warn('Domain sitemap validation warning:', domainValidation.error);
        }
      }
      
      // 5. Update verification details
      let status: 'success' | 'warning' | 'error' = 'success';
      let message = 'Verification completed successfully';
      
      if (!domainSitemapContent) {
        status = 'error';
        message = 'Domain sitemap is not accessible';
      } else if (storageSitemapContent && !compareSitemapContents(domainSitemapContent, storageSitemapContent)) {
        status = 'warning';
        message = 'Domain and storage sitemaps do not match';
      }
      
      setVerificationDetails({
        lastVerified: new Date().toISOString(),
        status,
        message
      });
      
    } catch (error) {
      console.error('Error verifying sitemap access:', error);
      toast({
        title: "Verification Error",
        description: `Could not verify sitemap: ${error instanceof Error ? error.message : String(error)}`,
        variant: "destructive",
      });
      
      setVerificationDetails({
        lastVerified: new Date().toISOString(),
        status: 'error',
        message: `Error: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return {
    isVerifying,
    verificationDetails,
    verifySitemapAccess
  };
};
